import { FormTemplate } from "components/modal/templates";
import { formatDate } from "helpers/date";
import { relationToTypesEnum, unitOfMeasuresEnum } from "helpers/enums";
import { getInitialStateFromConfig, serializeReq, validateForm } from "helpers/form";
import moment from "moment";
import React from "react";
import { alert } from "services/alert";
import { apply as applyRegistry } from "sources/books/registries";
import { config } from "./config";

const emptyNumberToNull = (value) => (value === "" || value === null || value === undefined ? null : value);

const createEmptyItem = () => ({
    key: `${Date.now()}-${Math.random()}`,
    id: 0,
    relatedToId: "",
    contractUniversalDocumentId: "",
    completedAt: "",
    quantity: 1,
    unitOfMeasure: unitOfMeasuresEnum.Piece,
    amount: "",
    vatRate: "",
    comment: "",
});

const formatItem = (item) => ({
    key: `${item.id || Date.now()}-${Math.random()}`,
    id: item.id || 0,
    relatedToId: item.relatedToId || "",
    contractUniversalDocumentId: item.contractUniversalDocumentId || "",
    completedAt: formatDate(item.completedAt),
    quantity: item.quantity ?? 1,
    unitOfMeasure: item.unitOfMeasure || unitOfMeasuresEnum.Piece,
    amount: item.amount ?? "",
    vatRate: item.vatRate ?? "",
    comment: item.comment || "",
});

const serializeItem = (item) => ({
    id: item.id || 0,
    relatedToId: emptyNumberToNull(item.relatedToId),
    relatedToType: item.relatedToId ? relationToTypesEnum.Bid : null,
    contractUniversalDocumentId: emptyNumberToNull(item.contractUniversalDocumentId),
    completedAt: formatDate(item.completedAt),
    quantity: emptyNumberToNull(item.quantity),
    unitOfMeasure: item.unitOfMeasure || unitOfMeasuresEnum.Piece,
    amount: item.amount,
    vatRate: emptyNumberToNull(item.vatRate),
    comment: item.comment || null,
});

class RegistryManualForm extends FormTemplate {
    close = (data = false) => {
        this.setState({ isLoading: false });
        this.props.back?.(data);
    };

    render = () => this.renderBody();

    isEdit = () => !!this.props.data?.id;

    isValidForm = (state) => {
        const serialized = serializeReq(state);
        return (
            validateForm(this.props.config, serialized) &&
            (serialized.items || []).every((item) => validateForm(this.props.config.items.config, item))
        );
    };

    setItemValue = (index, name, value) => {
        const items = this.state.items.map((item, itemIndex) =>
            itemIndex === index ? { ...item, [name]: value } : item,
        );
        const isValid = this.isValidForm({ ...this.state, items });
        this.setState({ items, ...(isValid ? { isValid: true } : {}) });
    };

    addItem = () => {
        const items = [...this.state.items, createEmptyItem()];
        const isValid = this.isValidForm({ ...this.state, items });
        this.setState({ items, ...(isValid ? { isValid: true } : {}) });
    };

    removeItem = (index) => {
        const items = this.state.items.filter((_, itemIndex) => itemIndex !== index);
        const isValid = this.isValidForm({ ...this.state, items });
        this.setState({ items, ...(isValid ? { isValid: true } : {}) });
    };

    validateBusinessRules = () => {
        if (!moment(this.state.to).isAfter(moment(this.state.from))) {
            alert({
                type: "warning",
                title: "Ошибка",
                message: "Дата окончания периода должна быть больше даты начала",
            });
            return false;
        }
        const invalidItemIndex = this.state.items.findIndex((item) => Number(item.amount) <= 0);
        if (invalidItemIndex !== -1) {
            alert({
                type: "warning",
                title: "Ошибка",
                message: `Укажите сумму больше нуля в строке ${invalidItemIndex + 1}`,
            });
            return false;
        }
        return true;
    };

    sendRequest = () => {
        if (!this.validateBusinessRules()) {
            this.setState({ isLoading: false });
            return Promise.resolve(false);
        }

        return applyRegistry({
            id: this.props.data?.id || 0,
            counterpartyId: this.state.counterpartyId,
            legalPersonId: emptyNumberToNull(this.state.legalPersonId),
            date: formatDate(this.state.date),
            from: formatDate(this.state.from),
            to: formatDate(this.state.to),
            comment: this.state.comment || null,
            items: this.state.items.map(serializeItem),
        });
    };

    renderItemField = (field, item, index, render = this.renderField) =>
        render({
            ...field,
            value: item[field.inputProps.name],
            onChange: ({ name, value }) => this.setItemValue(index, name, value),
        });

    renderItem = (item, index) => {
        const { config: itemConfig } = this.props.config.items;
        return (
            <div className="border rounded p-3 mb-3" key={item.key}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Строка {index + 1}</h6>
                    {!this.isEdit() && this.state.items.length > 1 && (
                        <button
                            type="button"
                            className="btn btn-xs btn-outline-danger"
                            onClick={() => this.removeItem(index)}
                        >
                            Удалить
                        </button>
                    )}
                </div>
                {this.renderItemField(itemConfig.relatedToId, item, index)}
                {this.renderItemField(itemConfig.contractUniversalDocumentId, item, index)}
                {this.renderItemField(itemConfig.completedAt, item, index, this.renderDateField)}
                {this.renderItemField(itemConfig.quantity, item, index)}
                {this.renderItemField(itemConfig.unitOfMeasure, item, index, this.renderSelect)}
                {this.renderItemField(itemConfig.amount, item, index)}
                {this.renderItemField(itemConfig.vatRate, item, index)}
                {this.renderItemField(itemConfig.itemComment, item, index)}
            </div>
        );
    };

    renderBodyInner = () => {
        const { counterpartyId, legalPersonId, date, from, to, comment } = this.props.config;
        return (
            <>
                {this.renderSelect(counterpartyId)}
                {this.renderSelect(legalPersonId)}
                {this.renderDateField(date)}
                {this.renderDateField(from)}
                {this.renderDateField(to)}
                {this.renderField(comment)}

                <div className="border-top pt-3 mt-2">
                    <h6>Перевозки</h6>
                    {this.state.items.map(this.renderItem)}
                    {!this.isEdit() && (
                        <button type="button" className="btn btn-sm btn-outline-primary mb-3" onClick={this.addItem}>
                            Добавить строку
                        </button>
                    )}
                </div>
            </>
        );
    };

    renderActionSend = () => {
        const cname = "btn btn-brand btn-sm modal-window__footer-action";
        return <input type="submit" className={cname} value={this.isEdit() ? "Сохранить" : "Создать реестр"} />;
    };
}

export default function ConfiguredRegistryManualForm(props) {
    const data = props.data || {};
    const isEdit = !!data.id;
    const initialItems = isEdit ? data.items || [] : data.items?.length ? data.items : [createEmptyItem()];
    const initialState = {
        ...getInitialStateFromConfig(config),
        counterpartyId: data.counterpartyId || data.counterparty?.id || "",
        legalPersonId: data.legalPersonId || data.legalPerson?.id || "",
        date: formatDate(data.date) || moment().format("YYYY-MM-DD"),
        from: formatDate(data.from),
        to: formatDate(data.to),
        comment: data.comment || "",
        items: initialItems.map(formatItem),
    };

    return <RegistryManualForm {...props} config={config} initialState={initialState} />;
}
